/**
 * Onboarding Checklist utilities.
 *
 * Defines the steps a new user should complete after signup.
 * Completion state stored in user.protectedData.onboarding:
 *   { completed: ['profile_photo', 'first_listing', ...], dismissed: false }
 */

export const CHECKLIST_STEPS = [
  {
    id: 'profile_photo',
    labelKey: 'Onboarding.profilePhoto',
    descriptionKey: 'Onboarding.profilePhotoDesc',
    url: '/profile-settings',
    required: true,
  },
  {
    id: 'profile_bio',
    labelKey: 'Onboarding.profileBio',
    descriptionKey: 'Onboarding.profileBioDesc',
    url: '/profile-settings',
    required: false,
  },
  {
    id: 'first_listing',
    labelKey: 'Onboarding.firstListing',
    descriptionKey: 'Onboarding.firstListingDesc',
    url: '/l/new',
    required: true,
  },
  {
    id: 'payment_method',
    labelKey: 'Onboarding.paymentMethod',
    descriptionKey: 'Onboarding.paymentMethodDesc',
    url: '/account/payments',
    required: true,
  },
  {
    id: 'notifications',
    labelKey: 'Onboarding.notifications',
    descriptionKey: 'Onboarding.notificationsDesc',
    url: '/account/notifications',
    required: false,
  },
];

/**
 * Calculate completion percentage.
 * @param {string[]} completed — array of completed step IDs
 * @returns {number} — 0-100
 */
export const getProgress = (completed = []) => {
  const required = CHECKLIST_STEPS.filter(s => s.required);
  const completedRequired = required.filter(s => completed.includes(s.id));
  return Math.round((completedRequired.length / required.length) * 100);
};

/**
 * Check if onboarding is complete (all required steps done).
 * @param {string[]} completed
 * @returns {boolean}
 */
export const isOnboardingComplete = (completed = []) => {
  return CHECKLIST_STEPS
    .filter(s => s.required)
    .every(s => completed.includes(s.id));
};

/**
 * Get incomplete steps.
 * @param {string[]} completed
 * @returns {Object[]}
 */
export const getIncompleteSteps = (completed = []) =>
  CHECKLIST_STEPS.filter(s => !completed.includes(s.id));

/**
 * Mark a step as completed (returns new completed array).
 * @param {string[]} current
 * @param {string} stepId
 * @returns {string[]}
 */
export const markStepComplete = (current = [], stepId) => {
  if (!CHECKLIST_STEPS.find(s => s.id === stepId)) {
    throw new Error(`Unknown step: ${stepId}`);
  }
  if (current.includes(stepId)) return current;
  return [...current, stepId];
};
